import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import {
  fieldFormating,
  headingHighlight,
  headingHighlightUser,
} from '../../utils/appTableUtil/AppTableUtil'
import {
  ActionHandler,
  PlatformRole,
  ActionDropdown,
  ColumnType,
} from '../../utils/types/UICommonTypes'
import { PERMISSIONS, WIDGET_TYPE } from '../common/auth/AuthorizeConstant'
import ATDropdownActionButton from '../common/formComponent/ATDropdownActionButton'
import {
  UserActionHandler,
  UserColumnType,
  UserEditHandler,
  UserViewActivityLogHandler,
  usersListData,
} from '../../interface/response/UserRolesResponse'
import { getWidgetMapping } from '../../utils/authUtil/AuthUtil'

export const USER_MANAGEMENT = Object.freeze({
  USER: { key: 1, name: 'common.users', eventKey: 'users' },
  PLATFORM_ROLE: {
    key: 2,
    name: 'common.platform_roles',
    eventKey: 'platform_roles',
  },
  MASTER_ROLE: {
    key: 3,
    name: 'common.master_roles',
    eventKey: 'master_roles',
  },
})

export const userSteps = [
  { label: `Basic Details` },
  { label: 'Select Role' },
  { label: 'Select Customer' },
]

export const usermanagementTabs = [
  {
    key: USER_MANAGEMENT.USER.key,
    eventKey: USER_MANAGEMENT.USER.eventKey,
    title: USER_MANAGEMENT.USER.name,
    icon: null,
    disabled: false,
    tooltip: USER_MANAGEMENT.USER.name,
  },
  {
    key: USER_MANAGEMENT.PLATFORM_ROLE.key,
    eventKey: USER_MANAGEMENT.PLATFORM_ROLE.eventKey,
    title: USER_MANAGEMENT.PLATFORM_ROLE.name,
    icon: null,
    disabled: false,
    tooltip: USER_MANAGEMENT.PLATFORM_ROLE.name,
  },
  {
    key: USER_MANAGEMENT.MASTER_ROLE.key,
    eventKey: USER_MANAGEMENT.MASTER_ROLE.eventKey,
    title: USER_MANAGEMENT.MASTER_ROLE.name,
    icon: null,
    disabled: false,
    tooltip: USER_MANAGEMENT.MASTER_ROLE.name,
  },
]

const getUserActions = (
  value: usersListData,
  onStatusHandler: UserActionHandler,
  onEditHandler: UserEditHandler,
  onViewActivityLogHandler: UserViewActivityLogHandler,
) => {
  return [
    {
      widgetType: WIDGET_TYPE.DROPDOWN_ITEM,
      name: 'userRoles.edit_user',
      functionName: () => onEditHandler(String(value?.userId)),
      permission: PERMISSIONS.EDIT_USER,
    },
    {
      widgetType: WIDGET_TYPE.DROPDOWN_ITEM,
      name: value?.userStatus ? 'userRoles.deactivate' : 'userRoles.activate',
      functionName: () => onStatusHandler(value),
      permission: PERMISSIONS.UPDATE_USER_STATUS,
    },
    {
      widgetType: WIDGET_TYPE.DROPDOWN_ITEM,
      name: 'userRoles.view_activity_log',
      functionName: () => onViewActivityLogHandler(value),
      permission: PERMISSIONS.VIEW_USERS,
    },
  ]
}

export const UserListColumn = (
  onStatusHandler: UserActionHandler,
  onEditHandler: UserEditHandler,
  onViewActivityLogHandler: UserViewActivityLogHandler,
): UserColumnType[] => [
  {
    key: 'userName',
    title: 'userRoles.user_name',
    style: { width: '70px' },
    render: (_, value) => headingHighlightUser(value?.userName, 'primary'),
  },
  {
    key: 'email',
    title: 'userRoles.user_email',
    style: { width: '70px' },
    render: (_, value) => fieldFormating(value?.email),
  },
  {
    key: 'phone',
    title: 'userRoles.user_phone',
    style: { width: '70px' },
    render: (_, value) => fieldFormating(value?.phone),
  },
  {
    key: 'role',
    title: 'userRoles.role',
    style: { width: '70px', position: 'relative' },
    render: (_, value) =>
      value?.role?.length > 0 ? (
        <OverlayTrigger
          placement="bottom"
          overlay={
            <Tooltip id={`tooltip-${value?.role}`} data-html={true}>
              {value?.role?.join('\n')}
            </Tooltip>
          }
        >
          <div className="textOverflow tooltip-format">
            {value?.role?.join('; ')}
          </div>
        </OverlayTrigger>
      ) : (
        '---'
      ),
  },
  {
    key: 'customer',
    title: 'userRoles.customer',
    style: { width: '70px' },
    render: (_, value) => (
      <>{value.customerName ? fieldFormating(value.customerName) : '---'}</>
    ),
  },
  {
    key: 'action',
    style: { width: '20px', textAlign: 'end' },
    render: (_, value) => (
      <div className="text-end">
        <ATDropdownActionButton
          id="userDropdownAction"
          size="sm"
          title={'common.actions'}
          actionArray={getUserActions(
            value,
            onStatusHandler,
            onEditHandler,
            onViewActivityLogHandler,
          )}
        />
      </div>
    ),
  },
]

const getRoleAction = (
  isMaster: boolean,
  value: PlatformRole,
  onEditHandler: ActionHandler,
  onDeleteHandler?: ActionHandler,
  viewRoleActivity?: (data: PlatformRole | undefined, show: boolean) => void,
  onRecoverHandler?: ActionHandler,
): ActionDropdown[] => {
  const editPermission = isMaster
    ? PERMISSIONS.EDIT_MASTER_ROLE
    : PERMISSIONS.EDIT_ROLE

  const activityLogAction: ActionDropdown | null = viewRoleActivity
    ? {
        widgetId: getWidgetMapping(PERMISSIONS.VIEW_ROLES),
        name: 'user_management.role_activity_log',
        functionName: () => viewRoleActivity(value, true),
        permission: PERMISSIONS.VIEW_ROLES,
      }
    : null

  // Deleted role in Platform Roles tab: Recover Role + Activity Log
  if (value.isActive === false && !isMaster) {
    return [
      onRecoverHandler && {
        widgetId: getWidgetMapping(PERMISSIONS.ROLE_RECOVERY),
        name: 'user_management.recover_role',
        functionName: () => onRecoverHandler(value),
        permission: PERMISSIONS.ROLE_RECOVERY,
      },
      activityLogAction,
    ].filter(Boolean) as ActionDropdown[]
  }

  return [
    {
      widgetId: getWidgetMapping(editPermission),
      name: 'user_management.edit_role_details',
      functionName: () => onEditHandler(value),
      permission: editPermission,
    },
    !isMaster &&
      onDeleteHandler &&
      value.numberOfUsers <= 0 && {
        widgetId: getWidgetMapping(PERMISSIONS.DELETE_ROLE),
        name: 'user_management.delete_role',
        functionName: () => onDeleteHandler(value),
        permission: PERMISSIONS.DELETE_ROLE,
      },
    activityLogAction,
  ].filter(Boolean) as ActionDropdown[]
}

export const RolesListColumn = (
  onDeleteHandler: ActionHandler,
  onEditHandler: ActionHandler,
  isMaster: boolean,
  viewRoleActivity: (data: PlatformRole | undefined, show: boolean) => void,
  onRecoverHandler?: ActionHandler,
  deletedLabel?: string,
): ColumnType[] => {
  const columns: ColumnType[] = [
    {
      key: 'roleName',
      title: 'user_management.role',
      style: { width: '50px' },
      render: (_, value) => (
        <div className="d-flex align-items-end gap-1">
          {headingHighlightUser(value?.roleName, 'primary')}
          {!isMaster && value?.isActive === false && deletedLabel && (
            <span className="text-danger fw-bold">{deletedLabel}</span>
          )}
        </div>
      ),
    },
    {
      key: 'description',
      title: 'user_management.description',
      style: { width: '150px' },
      render: (_, value) => fieldFormating(value?.description),
    },
    {
      key: 'name',
      title: 'user_management.application',
      style: { width: '50px' },
      render: (_, value) => fieldFormating(value?.name),
    },
  ]

  if (!isMaster) {
    columns.push({
      key: 'numberOfUsers',
      title: 'user_management.number_of_users',
      style: { width: '50px', textAlign: 'center' },
      render: (_, value) => headingHighlight(value?.numberOfUsers),
    })
  }

  columns.push({
    key: 'action',
    style: { width: '10px', textAlign: 'end' },
    render: (_, value) => (
      <div className="text-end">
        <ATDropdownActionButton
          id="roleDropdownAction"
          size="sm"
          title={'common.actions'}
          actionArray={getRoleAction(
            isMaster,
            value,
            onEditHandler,
            onDeleteHandler,
            viewRoleActivity,
            onRecoverHandler,
          )}
        />
      </div>
    ),
  })

  return columns
}

export const EVENT_TYPE = Object.freeze({
  STATUS_CHANGE: 1,
  DATA_CHANGE: 0,
  DATA_ADDITION: 2,
})
